import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import SearchInput from "../../components/Search/SearchInput";
import { IOrder } from "../../models/Order.model";
import {
  fetchPendingCreditRequests,
  fetchCreditHistory,
  approveCreditRequest,
} from "../../store/slices/orderSlice";
import PaginationControls from "../../utils/PaginationControls";
import { useSearchParams } from "react-router-dom";

interface CreditRequestCardProps {
  order: IOrder;
  isHistory?: boolean;
  onAction?: (
    orderId: string,
    status: "approved" | "rejected" | "cancelled",
    remark: string,
  ) => void;
}

const CreditRequestCard: React.FC<CreditRequestCardProps> = ({
  order,
  isHistory,
  onAction,
}) => {
  const [remark, setRemark] = useState("");
  const [showAction, setShowAction] = useState<
    "approved" | "rejected" | "cancelled" | null
  >(null);

  const handleSubmit = () => {
    if (showAction && onAction) onAction(order._id, showAction, remark);
  };

  const { userId } = useSelector((state: RootState) => state.auth);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex flex-col justify-between h-full hover:shadow-md transition-all">
      <div>
        <div className="flex justify-between items-start mb-2">
          <span className="text-xs font-bold text-slate-500">
            {order.orderNumber}
          </span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full capitalize border ${
              order.creditStatus === "approved"
                ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                : order.creditStatus === "rejected"
                  ? "bg-red-50 text-red-600 border-red-200"
                  : "bg-amber-50 text-amber-600 border-amber-200"
            }`}
          >
            {order.creditStatus}
          </span>
        </div>

        <h3 className="font-semibold text-sm text-slate-800 line-clamp-1">
          {typeof order.customerId === "object"
            ? order.customerId?.name
            : "N/A"}
        </h3>
        <p className="text-xs text-slate-500 mb-3 line-clamp-1">
          {typeof order.customerId === "object"
            ? order.customerId?.mobileNumber
            : ""}
        </p>

        <div className="space-y-1.5 text-xs text-slate-600 mb-4 bg-slate-50 p-2 rounded">
          <div className="flex justify-between">
            <span>
              {order.creditStatus === "approved"
                ? "Credited Amount:"
                : "Remaining Amount:"}
            </span>
            <span
              className={`font-medium ${order.creditStatus === "approved" ? "text-emerald-700" : "text-slate-800"}`}
            >
              ₹
              {order.creditStatus === "approved"
                ? (
                    order.creditApprovalHistory?.[0]?.approvedAmount || 0
                  ).toLocaleString()
                : (
                    order.totalAmount - order.paymentDetails?.paymentPaid || 0
                  ).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Req. By:</span>
            <span className="font-medium">
              {/* @ts-ignore */}
              {order.creditRequestedBy?.username || "Unknown"}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Date:</span>
            <span className="font-medium">
              {order.createdDate
                ? format(new Date(order.createdDate), "dd MMM")
                : "-"}
            </span>
          </div>
        </div>
      </div>

      {!isHistory && !showAction && onAction && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => setShowAction("approved")}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs py-2 rounded transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() =>
              setShowAction(
                order.creditRequestedBy?._id.toString() === userId
                  ? "cancelled"
                  : "rejected",
              )
            }
            className="flex-1 bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs py-2 rounded transition-colors"
          >
            {order.creditRequestedBy?._id.toString() === userId
              ? "Cancel"
              : "Reject"}
          </button>
        </div>
      )}

      {showAction && (
        <div className="mt-2 animate-in fade-in zoom-in-95 duration-200">
          <textarea
            autoFocus
            className="w-full text-xs border border-slate-300 rounded p-2 mb-2 focus:ring-1 focus:ring-slate-400 outline-none"
            placeholder={`Reason for ${showAction}...`}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              className={`flex-1 text-white text-xs py-1.5 rounded ${
                showAction === "approved" ? "bg-emerald-600" : "bg-red-600"
              }`}
            >
              Confirm
            </button>
            <button
              onClick={() => {
                setShowAction(null);
                setRemark("");
              }}
              className="flex-1 bg-slate-100 text-slate-600 text-xs py-1.5 rounded hover:bg-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CreditRequests: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const subTab =
    (searchParams.get("creditTab") as "pending" | "history") || "pending";

  const handleTabChange = (tab: "pending" | "history") => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("creditTab", tab);
    setSearchParams(newParams, { replace: true });
    setPage(1);
  };
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const user = useSelector((state: RootState) => state.auth);
  const isManager = ["admin", "sh", "bdm"].includes(user?.role || "");

  const { pendingCreditRequests, creditHistory, loading } = useSelector(
    (state: RootState) => state.order,
  );

  const activeSection =
    subTab === "pending" ? pendingCreditRequests : creditHistory;
  const currentList = activeSection.data;
  const pagination = activeSection.pagination;

  useEffect(() => {
    const params = { page, limit, search };
    if (subTab === "pending") {
      dispatch(fetchPendingCreditRequests(params));
    } else {
      dispatch(fetchCreditHistory(params));
    }
  }, [dispatch, subTab, page, search]);

  const handleDecision = async (
    orderId: string,
    status: "approved" | "rejected" | "cancelled",
    remark: string,
  ) => {
    try {
      await dispatch(
        approveCreditRequest({ orderId, status, remark }),
      ).unwrap();

      const params = { page, limit, search };
      if (subTab === "pending") {
        dispatch(fetchPendingCreditRequests(params));
      } else {
        dispatch(fetchCreditHistory(params));
      }
    } catch (err: any) {
      toast.error(err.message || "Action failed");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center">
        <div className="w-64">
          <SearchInput
            placeholder="Search name or mobile..."
            className="w-full text-xs p-2 border rounded"
            onSearch={(val) => {
              setSearch(val);
              setPage(1);
            }}
          />
        </div>
        <div className="flex bg-white p-1 rounded-md border border-slate-200 shadow-sm">
          <button
            onClick={() => {
              handleTabChange("pending");
              setPage(1);
            }}
            className={`px-4 py-1.5 text-xs font-medium rounded transition-all ${
              subTab === "pending"
                ? "bg-slate-800 text-white shadow"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Pending Requests
          </button>
          <button
            onClick={() => {
              handleTabChange("history");
              setPage(1);
            }}
            className={`px-4 py-1.5 text-xs font-medium rounded transition-all ${
              subTab === "history"
                ? "bg-slate-800 text-white shadow"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            History
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          Loading...
        </div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm bg-white rounded border border-dashed border-slate-300">
          No {subTab} requests found.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {currentList.map((order: IOrder) => (
              <CreditRequestCard
                key={order._id}
                order={order}
                isHistory={subTab === "history"}
                onAction={
                  !isManager || subTab === "history"
                    ? undefined
                    : handleDecision
                }
              />
            ))}
          </div>
          <PaginationControls
            currentPage={page}
            totalPages={pagination?.pages || 1}
            totalItems={pagination?.total || 0}
            onPageChange={(newPage) => setPage(newPage)}
            isLoading={loading}
          />
        </>
      )}
    </div>
  );
};

export default CreditRequests;
